/** A caption that may still be stored in the pre-migration locale-keyed object shape. */
export type LocalizedCaption = string | { [locale: string]: string } | null | undefined;

/**
 * Resolve an FLI caption to a display string.
 *
 * Historically `fli.caption` was a locale-keyed object `{ 'de-ch': …, 'en-us': … }`; a migration
 * converted it to a plain string (the value for the report's language). The migration did NOT run
 * for every report, so some captions are still objects — and wherever such a caption is rendered or
 * concatenated it shows "[object Object]" (and, on string operations like `.match` / `.toLowerCase`,
 * can throw and loop, e.g. on the print page).
 *
 * This is the single, display-/use-time guard: it returns the caption as-is when it is already a
 * string; otherwise it picks the first of `preferredLanguages` (typically the REPORT language, then
 * the app's current language) present on the object, falling back to the first available locale
 * value. It never returns "[object Object]" and never throws.
 *
 * @param caption the possibly-object caption.
 * @param preferredLanguages locale keys to try in order (e.g. reportLocale, currentLanguage).
 */
export function getCaptionString(
    caption: LocalizedCaption,
    ...preferredLanguages: Array<string | null | undefined>
): string {
    if (caption == null) return '';
    if (typeof caption === 'string') return caption;
    if (typeof caption === 'object') {
        for (const lang of preferredLanguages) {
            if (lang && typeof caption[lang] === 'string' && caption[lang] !== '') return caption[lang];
        }
        for (const value of Object.values(caption)) {
            if (typeof value === 'string' && value !== '') return value;
        }
    }
    return '';
}
